import { cidrsOverlap, parseCidr } from './ip';
import { isConsoleOwned, RESOURCE_NAMESPACE, type Namespace } from './ownership';
import { ResourceType, type ResourceSpec } from './desired-state';

/**
 * What a console would change that already exists, and what to do about it.
 *
 * Most edges are installed on hardware that was already doing something. The
 * console's intent will sometimes land on a tunnel, a route, or a firewall rule
 * an administrator wrote by hand, and the only acceptable behaviour is to notice
 * and stop. Silently overwriting is how an orchestrator takes down a site it was
 * meant to connect.
 *
 * Detection is therefore a read-only comparison run before anything is applied,
 * and its result is a decision the operator makes rather than a default the
 * console picks.
 */

/** How the console's intent collides with what is already on the edge. */
export const CONFLICT_KINDS = [
  /** A local object already carries the name the console would create. */
  'OBJECT_EXISTS',
  /** A local interface already holds an address the console would assign. */
  'ADDRESS_IN_USE',
  /** A local service already listens on the port the console would bind. */
  'PORT_IN_USE',
  /** A local route or interface covers a prefix the fabric would carry. */
  'PREFIX_OVERLAP',
  /** Another configuration manager owns the object. */
  'FOREIGN_MANAGER',
] as const;
export type ConflictKind = (typeof CONFLICT_KINDS)[number];

/**
 * Whether a conflict stops a deployment.
 *
 * `blocking` means applying would change something the console was not given,
 * so nothing is applied until the operator decides. `advisory` means the intent
 * can be carried out safely but the operator would want to know — an overlapping
 * prefix still routes, it just may not route where they expect.
 */
export type ConflictSeverity = 'blocking' | 'advisory';

const SEVERITY: Record<ConflictKind, ConflictSeverity> = {
  // Writing these would destroy or displace working configuration.
  OBJECT_EXISTS: 'blocking',
  ADDRESS_IN_USE: 'blocking',
  PORT_IN_USE: 'blocking',
  FOREIGN_MANAGER: 'blocking',
  // The fabric can carry an overlapping prefix; which way traffic goes is a
  // routing decision the operator should make knowingly, not a broken apply.
  PREFIX_OVERLAP: 'advisory',
};

export function severityOf(kind: ConflictKind): ConflictSeverity {
  return SEVERITY[kind];
}

/**
 * How an operator settles a conflict.
 *
 * There is deliberately no default. Each option loses something — local control,
 * console management, or the existing object — and which loss is acceptable is
 * not something the console can infer.
 */
export const CONFLICT_RESOLUTIONS = [
  /** Leave the object alone. The console stops trying to manage it. */
  'KEEP_LOCAL',
  /** Adopt the object as it stands, without changing it on the wire. */
  'IMPORT',
  /** Let the console overwrite it. */
  'REPLACE',
] as const;
export type ConflictResolution = (typeof CONFLICT_RESOLUTIONS)[number];

/**
 * Which resolutions make sense for a kind of conflict, most conservative first.
 *
 * A port cannot be shared, so adopting the object that already holds it does not
 * help — whatever the console wanted to bind still cannot bind. Every other kind
 * can be settled any of the three ways.
 */
export function offeredResolutions(kind: ConflictKind): ConflictResolution[] {
  return kind === 'PORT_IN_USE' ? ['KEEP_LOCAL', 'REPLACE'] : ['KEEP_LOCAL', 'IMPORT', 'REPLACE'];
}

/** An object the edge reported finding on itself. */
export interface ObservedObject {
  type: ResourceType;
  /** Local name or identifier, e.g. `wg0`, `LAN_to_WAN_allow`. */
  key: string;
  /** Set when something other than this console manages the object. */
  managedBy?: string | null;
  addresses?: string[];
  listenPort?: number | null;
  /** Prefixes this object routes or covers. */
  prefixes?: string[];
}

export interface Conflict {
  kind: ConflictKind;
  severity: ConflictSeverity;
  resourceType: ResourceType;
  /** The key the console intended to write. */
  resourceKey: string;
  /** The local object it collides with. */
  observedKey: string;
  namespace: Namespace;
  /** What overlapped, when the conflict is about a value rather than a name. */
  detail: string;
  /** Stated plainly, because this is what an operator reads first. */
  explanation: string;
  /** Resolutions that make sense for this conflict, most conservative first. */
  offered: ConflictResolution[];
}

export interface DetectInput {
  /** What the console intends to write. */
  desired: ResourceSpec[];
  /** What the edge reported already having. */
  observed: ObservedObject[];
}

/**
 * Compare intent against what is already on the edge.
 *
 * Pure and read-only by construction: it takes two lists and returns findings.
 * A detector that could change something would be a detector nobody could safely
 * run before deciding.
 */
export function detectConflicts(input: DetectInput): Conflict[] {
  const conflicts: Conflict[] = [];

  for (const spec of input.desired) {
    if (spec.absent === true) continue; // Removing something cannot collide with it.

    for (const object of input.observed) {
      if (object.type !== spec.type) continue;

      // An object this console created is not a conflict with itself. This is
      // what the origin stamp from ownership is for: without it, every
      // redeployment would look like a collision with its own previous output.
      if (isConsoleOwned(object.key)) continue;

      const namespace = RESOURCE_NAMESPACE[spec.type];

      if (object.managedBy !== undefined && object.managedBy !== null && object.managedBy !== '') {
        conflicts.push({
          kind: 'FOREIGN_MANAGER',
          severity: severityOf('FOREIGN_MANAGER'),
          resourceType: spec.type,
          resourceKey: spec.key,
          observedKey: object.key,
          namespace,
          detail: object.managedBy,
          explanation:
            `${object.key} is managed by ${object.managedBy}. Two managers writing the same ` +
            'object will each undo the other, so the console will not touch it until one of ' +
            'them is given the object.',
          offered: offeredResolutions('FOREIGN_MANAGER'),
        });
        continue;
      }

      if (object.key === spec.key) {
        conflicts.push({
          kind: 'OBJECT_EXISTS',
          severity: severityOf('OBJECT_EXISTS'),
          resourceType: spec.type,
          resourceKey: spec.key,
          observedKey: object.key,
          namespace,
          detail: object.key,
          explanation:
            `${object.key} already exists on this edge and was not created by this console. ` +
            'Applying would overwrite it.',
          offered: offeredResolutions('OBJECT_EXISTS'),
        });
        continue;
      }

      const desiredAddresses = stringList(spec.spec.addresses);
      const sharedAddress = desiredAddresses.find((a) => (object.addresses ?? []).includes(a));
      if (sharedAddress !== undefined) {
        conflicts.push({
          kind: 'ADDRESS_IN_USE',
          severity: severityOf('ADDRESS_IN_USE'),
          resourceType: spec.type,
          resourceKey: spec.key,
          observedKey: object.key,
          namespace,
          detail: sharedAddress,
          explanation:
            `${sharedAddress} is already configured on ${object.key}. Assigning it again would ` +
            'take the address from whatever is using it.',
          offered: offeredResolutions('ADDRESS_IN_USE'),
        });
        continue;
      }

      const desiredPort = numberOrNull(spec.spec.listenPort);
      if (desiredPort !== null && object.listenPort === desiredPort) {
        conflicts.push({
          kind: 'PORT_IN_USE',
          severity: severityOf('PORT_IN_USE'),
          resourceType: spec.type,
          resourceKey: spec.key,
          observedKey: object.key,
          namespace,
          detail: String(desiredPort),
          explanation: `Port ${desiredPort} is already bound by ${object.key}. Both cannot listen on it.`,
          offered: offeredResolutions('PORT_IN_USE'),
        });
        continue;
      }

      const overlap = firstPrefixOverlap(stringList(spec.spec.prefixes), object.prefixes ?? []);
      if (overlap !== null) {
        conflicts.push({
          kind: 'PREFIX_OVERLAP',
          severity: severityOf('PREFIX_OVERLAP'),
          resourceType: spec.type,
          resourceKey: spec.key,
          observedKey: object.key,
          namespace,
          detail: overlap.comparable
            ? `${overlap.desired} overlaps ${overlap.observed}`
            : `${overlap.desired} could not be compared with ${overlap.observed}`,
          explanation: overlap.comparable
            ? `${overlap.desired} overlaps ${overlap.observed}, already routed by ${object.key}. ` +
              'Traffic for the overlapping range may not take the path you expect.'
            : `${overlap.observed} on ${object.key} is not a prefix this console can parse, so ` +
              `whether it overlaps ${overlap.desired} could not be determined. Check it by hand.`,
          offered: offeredResolutions('PREFIX_OVERLAP'),
        });
      }
    }
  }

  return conflicts;
}

/** Whether anything found stops a deployment. */
export function hasBlockingConflict(conflicts: Conflict[]): boolean {
  return conflicts.some((c) => c.severity === 'blocking');
}

/**
 * What a resolution does to the ownership policy and the object.
 *
 * `IMPORT` is the option that has to be got right. Adopting an object and then
 * immediately rewriting it to match the desired spec would be `REPLACE` wearing
 * a friendlier name, so importing records what is actually on the device as the
 * accepted state. If the desired spec differs, that difference becomes an
 * ordinary planned change the operator can see and approve — not something that
 * happens as a side effect of saying yes to adoption.
 */
export interface ResolutionEffect {
  /** Whether the console may write this object afterwards. */
  consoleManages: boolean;
  /** Whether the object's current contents are kept as-is. */
  preservesCurrentState: boolean;
  /** Whether applying the resolution changes anything on the edge now. */
  changesEdgeNow: boolean;
  summary: string;
}

export const RESOLUTION_EFFECTS: Record<ConflictResolution, ResolutionEffect> = {
  KEEP_LOCAL: {
    consoleManages: false,
    preservesCurrentState: true,
    changesEdgeNow: false,
    summary:
      'The object stays exactly as it is and the console stops trying to manage it. The rest ' +
      'of the fabric still deploys.',
  },
  IMPORT: {
    consoleManages: true,
    preservesCurrentState: true,
    changesEdgeNow: false,
    summary:
      'The console adopts the object as it currently stands. Nothing changes on the edge now; ' +
      'any difference from the intended configuration is shown as a normal pending change.',
  },
  REPLACE: {
    consoleManages: true,
    preservesCurrentState: false,
    changesEdgeNow: true,
    summary:
      'The console overwrites the existing object with its own configuration. Whatever was ' +
      'there is lost.',
  },
};

/** Resolutions that never alter the edge, for callers that want the safe set. */
export const NON_DESTRUCTIVE_RESOLUTIONS: readonly ConflictResolution[] =
  CONFLICT_RESOLUTIONS.filter((r) => !RESOLUTION_EFFECTS[r].changesEdgeNow);

function stringList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

function numberOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function firstPrefixOverlap(
  desired: string[],
  observed: string[],
): { desired: string; observed: string; comparable: boolean } | null {
  let uncomparable: { desired: string; observed: string } | null = null;

  for (const d of desired) {
    const dCidr = parseCidr(d);
    for (const o of observed) {
      const oCidr = parseCidr(o);
      if (dCidr === null || oCidr === null) {
        // A prefix that will not parse cannot be compared, and "cannot compare"
        // is not the same as "no overlap". Inventory is validated on ingest so
        // this should not happen; if it does, the operator is told rather than
        // handed a silent all-clear that was never actually checked.
        uncomparable ??= { desired: d, observed: o };
        continue;
      }
      // `cidrsOverlap` treats containment as overlap, which is the case that
      // actually surprises people: a /16 route quietly swallowing a /24.
      if (cidrsOverlap(dCidr, oCidr)) return { desired: d, observed: o, comparable: true };
    }
  }
  return uncomparable === null ? null : { ...uncomparable, comparable: false };
}

/** Resource types an edge can meaningfully report existing objects for. */
export const IMPORTABLE_TYPES: readonly ResourceType[] = [
  ResourceType.WIREGUARD,
  ResourceType.FRR,
  ResourceType.ROUTE,
  ResourceType.NFTABLES,
  ResourceType.POLICY_RULE,
];
